<template>
  <div>
    <confirm-box :dialog="dialog" @confirmed="deletePatient" @cancel="cancel" />

    <v-data-table
      v-if="!loading"
      :headers="headers"
      :items="patients"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :page.sync="page"
      item-key="id"
      hide-default-footer
      hide-default-header
    >
      <template v-slot:top>
        <v-toolbar flat class="toolbar-custom">
          <v-toolbar-title class="toolbar-title">
            <v-icon class="mr-2" color="#aa00ff">mdi-account-group</v-icon>
            Patients
          </v-toolbar-title>
          <v-divider class="mx-3" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-btn
            @click="$router.push({
              name:'patient',
              params:{township_id:$route.params.township_id}
            })"
            class="action-btn mr-2"
            large
            rounded
          >
            <v-icon left>mdi-refresh</v-icon>View All
          </v-btn>
          <v-btn
            @click="showFilterModal = true"
            class="action-btn-primary mr-2"
            large
            rounded
          >
            <v-icon left>mdi-filter-variant</v-icon>Filter
          </v-btn>
          <v-btn
            @click="$router.push({
              name:'patient-create',
              params:{township_id:$route.params.township_id}
            })"
            class="action-btn-success"
            large
            rounded
          >
            <v-icon left>mdi-plus-circle-outline</v-icon>New Item
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:header="{ props: { headers } }">
        <thead class="table-header">
          <tr>
            <th
              v-for="header in headers"
              :key="header.text"
              class="table-header-cell"
            >
              {{ header.text }}
            </th>
          </tr>
        </thead>
      </template>

      <template v-slot:item="{ index, item }">
        <tr class="table-row">
          <td>{{ page > 1 ? (page - 1) * 30 + (index + 1) : index + 1 }}</td>
          <td>{{ item.name }}</td>
          <td>
            <span v-if="item.mdrtb_reg_no">
              {{ item.mdrtb_reg_no }}
            </span>
          </td>
          <td>
            <span v-if="item.mdrtb_reg_year">
              {{ item.mdrtb_reg_year }}
            </span>
          </td>
          <td v-if="item.sex == 1">Male</td>
          <td v-else>Female</td>
          <td>{{ calculateAge(item.dob)}}</td>
          <td>
            <router-link
              v-if="item.fm_status"
              :to="`/patient-family/${item.id}`"
              class="text-primary"
            >
              M: {{ item.fm_status.male || 0 }} /
              F: {{ item.fm_status.female || 0 }}
            </router-link>

            <span v-else>--</span>
          </td>

          <td v-if="item.pre_enroll == 1">Yes</td>
          <td v-else>No</td>

          <td v-if="item.mdrtb_monopoly==1">No</td>
          <td v-else-if="item.mdrtb_monopoly==2">Yes</td>
          <td v-else-if="item.mdrtb_monopoly==3">SG</td>
          <td v-else-if="item.mdrtb_monopoly==4">MGY</td>
          <td v-else-if="item.mdrtb_monopoly==5">TGI</td>
          <td v-else>--</td>

          <td v-if="item.is_auto_nominated==true">Yes</td>
          <td v-else>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <span v-on="on" v-bind="attrs"> Treatment Finished</span>
              </template>
              <span>{{ item.finished_date }}</span>
            </v-tooltip>
          </td>
          <td>
            <span
              v-if="item.outside_tsp == 2 && formatOutsideTownshipName(item)"
            >
              {{ item.township }} ({{ formatOutsideTownshipName(item) }})
            </span>
            <span v-else>{{ item.township }}</span>
          </td>
        
          <td v-if="item.remark">{{ item.remark }}</td>
          <td v-else>--</td>
          <td>
            <div class="mr-2">
              <v-btn
                class="white--text"
                depressed
                icon
                :to="`/enroll/clu/${item.id}`"
                v-if="item.diff_finished_change >= 30"
                ><v-icon color="red darken-2"> mdi-message-text </v-icon></v-btn
              >
              <v-btn
                @click="goUpdateForm(item)"
                icon
                class="mr-2 edit-btn"
                depressed
              >
                <v-icon color="white"> mdi-pencil-outline </v-icon>
              </v-btn>
              <v-btn
                @click="openConfrimBox(item)"
                icon
                color="red lighten-1"
                class="white--text"
                depressed
              >
                <v-icon> mdi-delete-outline </v-icon>
              </v-btn>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
    <div class="text-center py-3 pagination-container">
      <v-pagination
        :length="pageCount"
        v-model="page"
        :total-visible="7"
        color="#aa00ff"
        class="custom-pagination"
      ></v-pagination>
    </div>
    <filter-box
      v-bind:dialog="showFilterModal"
      @cancel="showFilterModal = false"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import Request from "@/services/Request";
import ResponseChecker from "@/services/ResponseChecker";
import ConfirmBox from "@/components/notification/ConfirmBox";
import FilterBox from "./components/FilterBox.vue";
import { CalculateAge } from "../../mixins/CalculateAge";

const outsideTownshipsUrl = "outside_township/get?";

function outsideTownshipLabelFromList(list, rawId) {
  if (rawId == null || rawId === "") return "";
  const key = String(rawId);
  const row = list.find(
    (r) =>
      String(r.id) === key ||
      (r.outside_tsp != null && String(r.outside_tsp) === key)
  );
  if (row && row.name) return row.name;
  return typeof rawId === "string" ? rawId : "";
}

export default {
  props: {
    patients: {
      type: Array,
    },
  },
  mixins: [CalculateAge],

  components: {
    ConfirmBox,
    FilterBox,
  },
  created() {
    if (!this.$route.query.page) {
      this.page = 1;
    } else {
      this.page = parseInt(this.$route.query.page);
    }
    this.fetchOutsideTownship();
  },
  data: () => ({
    showFilterModal: false,
    page: 0,
    itemsPerPage: 30,
    item: {},
    dialog: false,
    outsideTownshipsFromApi: [],
    headers: [
      { text: "No", value: "no", width: "300px", fixed: true },
      { text: "Name", value: "name", width: "300px" },
      { text: "Code", value: "code", width: "300px" },
      { text: "Year", value: "year", width: "300px" },
      { text: "Sex", value: "sex", width: "25%" },
      { text: "Age", value: "age", width: "25%" },
      { text: "Family Member", value: "fm_status" },
      { text: "Pre Enroll", value: "pre_enroll" },
      { text: "Mono-Poly", value: "monopoly" },
      { text: "Nominated", value: "Nominated" },
      { text: "Township", value: "township" },
      { text: "Remark", value: "support_remark" },

      { text: "Actions", value: "actions" },
    ],
  }),
  computed: {
    ...mapState("Loading", ["loading"]),
    ...mapState("Patient", ["pageCount"]),
    ...mapState("Admin", ["admin"]),
  },
  watch: {
    "$route.params.township_id"() {
      this.fetchOutsideTownship();
    },
    page(newValue) {
      const currentPage = this.$route.query.page
        ? parseInt(this.$route.query.page)
        : 1;

      // Avoid redundant navigation to the same route/page
      if (
        this.$route.name === "patient" &&
        currentPage === newValue &&
        this.$route.params.township_id === this.$route.params.township_id
      ) {
        return;
      }

      this.$router
        .push({
          name: "patient",
          params: { township_id: this.$route.params.township_id },
          query: {
            page: newValue,
          },
        })
        .catch((err) => {
          // Ignore NavigationDuplicated errors
          if (err && err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
    },
  },
  methods: {
    async fetchOutsideTownship() {
      const tid = this.$route.params.township_id;
      if (tid == null || tid === "") {
        this.outsideTownshipsFromApi = [];
        return;
      }
      const response = await Request.get_data(outsideTownshipsUrl, {
        township_id: tid,
        outside_tsp: tid,
      });
      if (!response) {
        this.outsideTownshipsFromApi = [];
        return;
      }
      const checker = new ResponseChecker(response);
      checker.checkResponse();
      if (!checker.isResponseStatusSuccess()) {
        this.outsideTownshipsFromApi = [];
        return;
      }
      const body = response.data;
      const list = Array.isArray(body) ? body : (body?.data ?? []);
      this.outsideTownshipsFromApi = Array.isArray(list) ? list : [];
    },
 
    formatOutsideTownshipName(item) {
      if (!item) return "";
      if (item.outside_township && item.outside_township.name) {
        return item.outside_township.name;
      }
      if (item.outside_township_name) {
        return item.outside_township_name;
      }
      const list = this.outsideTownshipsFromApi;
      let name = outsideTownshipLabelFromList(list, item.outside_tsp_name);
      if (name) return name;
      if (
        item.outside_tsp != null &&
        item.outside_tsp !== "" &&
        Number(item.outside_tsp) !== 1
      ) {
        name = outsideTownshipLabelFromList(list, item.outside_tsp);
      }
      return name || "";
    },
    openConfrimBox(item) {
      this.item = item;
      this.dialog = !this.dialog;
    },
    deletePatient() {
      let data = {
        id: this.item.id,
      };
      this.$store
        .dispatch("Patient/deletePatient", data)
        .then(() => (this.dialog = !this.dialog));
    },
    cancel() {
      this.dialog = !this.dialog;
    },
    goUpdateForm(item) {
      let convertNumber = parseInt(item.id);
      this.$router.push({
        name: "patient-edit",
        params: {
          id: convertNumber,
          township_id:this.$route.params.township_id
        },
      });
    },
  },
};
</script>

<style scoped>
.toolbar-custom {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 2px solid #e0e0e0;
  padding: 6px 16px;
  min-height: 70px !important;
  height: 70px !important;
}

.toolbar-title {
  font-size: 20px;
  font-weight: 600;
  color: #424242;
  display: flex;
  align-items: center;
}

/* Button Styling */
.action-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.action-btn-primary {
  background: linear-gradient(135deg, #aa00ff 0%, #8e00cc 100%) !important;
  color: white !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(170, 0, 255, 0.3);
  transition: all 0.3s ease;
}

.action-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(170, 0, 255, 0.4);
}

.action-btn-success {
  background: linear-gradient(135deg, #00c853 0%, #00a844 100%) !important;
  color: white !important;
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 200, 83, 0.3);
  transition: all 0.3s ease;
}

.action-btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 200, 83, 0.4);
}

/* Table Header Styling */
/* .table-header {
  background: linear-gradient(135deg, #aa00ff 0%, #8e00cc 100%);
} */

/* .table-header-cell {
  background: linear-gradient(135deg, #aa00ff 0%, #8e00cc 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 12px !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2) !important;
}

.table-header-cell:first-child {
  border-top-left-radius: 8px;
}

.table-header-cell:last-child {
  border-top-right-radius: 8px;
} */

/* Table Row Styling */
.table-row {
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: #f5f5f5 !important;
}

.table-row:nth-child(even) {
  background-color: #fafafa;
}

.table-row:nth-child(even):hover {
  background-color: #f0f0f0 !important;
}

.table-row td {
  padding: 12px !important;
  border-bottom: 1px solid #e0e0e0;
}

/* Edit Button */
.edit-btn {
  background: linear-gradient(135deg, #ff6f00 0%, #e65100 100%) !important;
  transition: all 0.3s ease;
}

.edit-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(255, 111, 0, 0.4);
}

/* Pagination Styling */
.pagination-container {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.custom-pagination >>> .v-pagination__item {
  color: #aa00ff;
  font-weight: 500;
}

.custom-pagination >>> .v-pagination__item--active {
  background: #aa00ff !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(170, 0, 255, 0.3);
}

.custom-pagination >>> .v-pagination__navigation {
  color: #aa00ff;
}

.custom-pagination >>> .v-pagination__navigation:hover {
  background: rgba(170, 0, 255, 0.1);
}

/* Responsive Design */
@media (max-width: 960px) {
  .toolbar-title {
    font-size: 18px;
  }
  
  .action-btn,
  .action-btn-primary,
  .action-btn-success {
    font-size: 12px;
    padding: 8px 12px;
  }
  
  .table-header-cell {
    font-size: 12px !important;
    padding: 12px 8px !important;
  }
}
</style>
